import { motion } from 'framer-motion'
import { useScrollAnimation } from '../../hooks/useScrollAnimation'
import styles from './Architecture.module.css'

const layers = [
  {
    title: 'Client Layer',
    items: [
      { label: 'npm install luminox-sdk', subtitle: 'Available on npm', type: 'typescript' },
      { label: 'Python SDK', subtitle: 'Install from repo', type: 'python' },
      { label: 'docker compose up', subtitle: 'Run from repo', type: 'cli' },
    ],
  },
  {
    title: 'Luminox Backend',
    items: [
      { label: 'API Layer', subtitle: 'Go/Gin • localhost:8029', type: 'api' },
      { label: 'Core Logic', subtitle: 'Python/FastAPI', type: 'core' },
    ],
  },
  {
    title: 'Infrastructure',
    items: [
      { label: 'PostgreSQL', type: 'infra' },
      { label: 'Redis', type: 'infra' },
      { label: 'RabbitMQ', type: 'infra' },
      { label: 'S3', type: 'infra' },
      { label: 'Web UI', type: 'infra' },
    ],
  },
]

export default function Architecture() {
  const { ref, isVisible } = useScrollAnimation<HTMLElement>()

  return (
    <section id="architecture" className={styles.architecture} ref={ref}>
      <motion.div
        className="section-header"
        initial={{ opacity: 0, y: 20 }}
        animate={isVisible ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.5 }}
      >
        <span className="section-tag">How It Works</span>
        <h2 className="section-title">Powerful Three-Layer Architecture</h2>
      </motion.div>

      <div className={styles.diagram}>
        {layers.map((layer, layerIndex) => (
          <motion.div key={layer.title}>
            <motion.div
              className={styles.layer}
              initial={{ opacity: 0, y: 30 }}
              animate={isVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: layerIndex * 0.2 }}
            >
              <h4>{layer.title}</h4>
              <div className={`${styles.items} ${layer.title === 'Infrastructure' ? styles.infra : ''}`}>
                {layer.items.map((item, itemIndex) => (
                  <motion.div
                    key={item.label}
                    className={`${styles.item} ${styles[item.type]}`}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={isVisible ? { opacity: 1, scale: 1 } : {}}
                    transition={{ duration: 0.3, delay: layerIndex * 0.2 + itemIndex * 0.1 }}
                    whileHover={{
                      scale: 1.05,
                      boxShadow: '0 10px 30px rgba(255, 215, 0, 0.2)',
                    }}
                  >
                    {'subtitle' in item ? (
                      <>
                        <span>{item.label}</span>
                        <small>{item.subtitle}</small>
                      </>
                    ) : (
                      <code>{item.label}</code>
                    )}
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {layerIndex < layers.length - 1 && (
              <motion.div
                className={styles.arrow}
                initial={{ opacity: 0 }}
                animate={isVisible ? { opacity: 0.5 } : {}}
                transition={{ duration: 0.5, delay: layerIndex * 0.2 + 0.3 }}
              >
                <motion.svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  animate={{ y: [0, 5, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  <path d="M12 5v14M5 12l7 7 7-7"/>
                </motion.svg>
              </motion.div>
            )}
          </motion.div>
        ))}
      </div>
    </section>
  )
}
